"use client";
import React, { useEffect, useRef, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import AboutHeroSection from "@/components/AboutHeroSection";
import contact from "../../public/assets/images/contact/contact.png";
import { Col, Container, Row } from "react-bootstrap";
import CommonInput from "@/components/CommonInput";
import CommonButton from "@/components/CommonButton";
import { faLinkedinIn, faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import "../app/contact/contact.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Call, Location } from "@/components/svg";
import Link from "next/link";
import ReCAPTCHA from "react-google-recaptcha";
import { showToast } from "../../utils/libs/toast-utilis";
import isDisposableEmail from "is-disposable-email";
import { userInquiry } from "@/services/pricing";
import { event } from "@/lib/analytics";
import { faCalendarDays, faEnvelope } from "@fortawesome/free-solid-svg-icons";
import { usePathname } from "next/navigation";
import PhoneInput from "react-phone-input-2";
import Lottie from "lottie-react";
import partyPopper from "../assets/party-popper.json";

const ContactPage = () => {
  const formRef = useRef(null);
  const [capVal, setCapVal] = useState(null);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const recaptchaRef = useRef(null);
  const pathname = usePathname();

  useEffect(() => {
    const hash = window.location.hash.replace("#", "");
    const scrollToSection = () => {
      if (hash === "inquiry" && formRef.current) {
        formRef.current.scrollIntoView({ behavior: "smooth" });
      }
    };
    const timeout = setTimeout(scrollToSection, 300);
    return () => clearTimeout(timeout);
  }, [pathname]);
  const initialValues = {
    name: "",
    lastname: "",
    email: "",
    message: "",
    projectdetails: "",
    number: "",
  };

  const validationSchema = Yup.object({
    name: Yup.string().required("First Name is Required"),
    lastname: Yup.string().required("Last Name is Required"),
    email: Yup.string().email("Invalid email").required("Email is Required"),
    message: Yup.string().required("Message is required"),
    number: Yup.string()
      .required("Mobile number is required")
      .test("is-valid", "Enter a valid phone number", (value) => {
        const digitsOnly = value?.replace(/\D/g, "");
        return digitsOnly && digitsOnly.length >= 10 && digitsOnly.length <= 15;
      }),
  });

  const handleSubmit = async (values, { resetForm }) => {
    if (!capVal) {
      showToast.error("Please complete the ReCAPTCHA.");
      return;
    }
    if (isDisposableEmail(values?.email)) {
      showToast.error("Please use a valid personal or business email address.");
      setError("Please use a valid personal or business email address.");
      return;
    }
    try {
      await userInquiry(values);
      showToast.success("Your message has been sent successfully!");
      setFormSubmitted(true);
    } catch (error) {
      if (error?.message?.length > 0) {
        showToast.error(error?.message);
      }
    }
    event({
      action: "user-inquiry",
      params: {
        page: "contact_page",
        name: `${values?.name} ${values?.lastname}`,
        email: values?.email,
      },
    });
    recaptchaRef?.current?.reset();
    resetForm();
    setCapVal(null);
  };
  const handleNewInquiry = () => {
    setFormSubmitted(false);
  };
  return (
    <>
      <AboutHeroSection
        pillText="~ CONTACT US ~"
        title="How Can We"
        highlight="Help?"
        description="Your vision fuels our passion. Connect with Oceanmtech, where we breathe life into your ideas. Reach out today and discover how our expertise can elevate your business to unprecedented heights."
        imageAlt="contact us"
        image={contact}
      />

      <div className="py-5" ref={formRef} id={"inquiry"}>
        <Container>
          <Row className="gy-4">
            <Col lg={6} md={12} className="d-flex">
              {!formSubmitted ? (
                <Formik
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                >
                  {({
                    values,
                    handleChange,
                    handleBlur,
                    isSubmitting,
                    setFieldValue,
                    touched,
                    errors,
                  }) => (
                    <Form className="w-100">
                      <Row className="gy-2">
                        <Col md={6} sm={12}>
                          <CommonInput
                            label="First Name"
                            name="name"
                            placeholder="First Name"
                            value={values.name}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className="contact-input"
                            error={touched.name && errors.name}
                          />
                        </Col>
                        <Col md={6} sm={12}>
                          <CommonInput
                            label="Last Name"
                            name="lastname"
                            placeholder="Last Name"
                            value={values.lastname}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className="contact-input"
                            error={touched.lastname && errors.lastname}
                          />
                        </Col>
                        <Col md={6} sm={12}>
                          <CommonInput
                            label="Email"
                            name="email"
                            placeholder="you@gmail.com"
                            value={values.email}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className="contact-input"
                            error={touched.email && errors.email}
                          />
                        </Col>
                        <Col md={6} sm={12}>
                          <div className="des-text">
                            <div className="contact-label">Mobile Number</div>
                            <PhoneInput
                              country={"in"}
                              value={values.number}
                              onChange={(value) =>
                                setFieldValue("number", value)
                              }
                              inputClass="w-100 price-input"
                              inputStyle={{ width: "100%" }}
                              containerStyle={{ width: "100%" }}
                              countryCodeEditable={true}
                              placeholder="Enter your mobile number"
                              inputProps={{
                                onBlur: handleBlur,
                                name: "number",
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  handleSubmit();
                                }
                              }}
                            />
                            {touched.number && errors.number && (
                              <div className="invalid-feedback d-block">
                                {errors.number}
                              </div>
                            )}
                          </div>
                        </Col>
                        <Col sm={12}>
                          <CommonInput
                            label="Message"
                            name="message"
                            placeholder="Leave your message to us"
                            isTextArea
                            value={values.message}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className="contact-input"
                            error={touched.message && errors.message}
                          />
                        </Col>
                        <Col sm={12}>
                          <ReCAPTCHA
                            ref={recaptchaRef}
                            sitekey={process.env.NEXT_PUBLIC_GOOGLE_RECAPTCHA}
                            onChange={(val) => setCapVal(val)}
                          />
                        </Col>
                        <Col sm={12} className="mt-3 d-flex gap-2">
                          <CommonButton
                            label="Send Message"
                            type="submit"
                            disabled={!capVal || isSubmitting}
                          />
                        </Col>
                      </Row>
                    </Form>
                  )}
                </Formik>
              ) : (
                <div className="thank-you-message text-center w-100">
                  <div className="w-100">
                    <div
                      className="animation-wrapper"
                      style={{ maxWidth: 400, margin: "0 auto" }}
                    >
                      <Lottie animationData={partyPopper} />
                    </div>
                    <h4 className="tnk-text">Thank you!</h4>
                    <p className="tnk-p-tag">
                      For contacting Neoverce IT Solutions! We appreciate you
                      taking the time to reach out to us. Your message is
                      important to us, and our team will review your inquiry
                      carefully. We typically respond within{" "}
                      <strong>24-48 hours</strong> during business days.
                    </p>
                    <CommonButton
                      label="Add Another Inquiry"
                      onClick={handleNewInquiry}
                      className="mx-auto"
                    />
                  </div>
                </div>
              )}
            </Col>

            {/* Right Side */}
            <Col lg={6} md={12}>
              <div className="contact-right">
                <h3>Chat with us</h3>
                <p className="mb-2">Speak to our friendly team via chat</p>

                <div className="d-flex align-items-center gap-2 mb-2">
                  <div className="d-block">
                    <FontAwesomeIcon
                      icon={faLinkedinIn}
                      size="lg"
                      className="contact-icon"
                    />
                  </div>
                  <Link
                    href="https://www.linkedin.com/company/neoverce-it-solutions-llp/"
                    target="_blank"
                    aria-label={`Massage on LinkedIn`}
                  >
                    <span>Message us on LinkedIn</span>
                  </Link>
                </div>

                <div className="d-flex align-items-center gap-2 mb-2">
                  <div className="d-block">
                    <FontAwesomeIcon
                      icon={faWhatsapp}
                      size="lg"
                      className="contact-icon"
                    />
                  </div>
                  <Link
                    href="https://wa.me/918200872703?text=Hi%20Neoverce%20Team,%20I%20would%20like%20to%20inquire%20about%20your%20services."
                    target="_blank"
                    aria-label={`Message us on WhatsApp`}
                  >
                    <span>Message us on WhatsApp</span>
                  </Link>
                </div>

                <div className="d-flex align-items-center gap-2 mb-2">
                  <div className="d-block">
                    <FontAwesomeIcon
                      icon={faEnvelope}
                      size="lg"
                      className="contact-icon"
                    />
                  </div>
                  <a
                    href="https://mail.google.com/mail/?view=cm&to=info@neoverce.com&su=Inquiry&body=Hi%20Neoverce%20Team,"
                    target="_blank"
                    aria-label={`Message us via Email`}
                  >
                    <span>Message us via Email</span>
                  </a>
                </div>
              </div>
              <div className="contact-right">
                <h3>Call us</h3>
                <p className="mb-2">Call our team Mon–Fri from 9am to 6pm IST</p>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <div className="d-block">
                    <FontAwesomeIcon
                      icon={faCalendarDays}
                      size="lg"
                      className="contact-icon"
                    />
                  </div>
                  <a
                    href="https://calendly.com/greet_at_neoverce/30min"
                    target="_blank"
                    aria-label={`call`}
                  >
                    <span>Schedule a call with us</span>
                  </a>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <div className="d-block">
                    <Call className="contact-icon" />
                  </div>
                  <Link href="tel:8200872703" aria-label={`Call Now`}>
                    <span>+91 820-0872703</span>
                  </Link>
                </div>
              </div>

              <div className="contact-right">
                <h3>Visit us</h3>
                <p className="mb-2">Chat to us in person at Surat</p>
                <div className="d-flex align-items-center gap-2">
                  <div className="d-block">
                    <Location className="contact-icon" />
                  </div>
                  <Link
                    href="https://maps.app.goo.gl/cULZb7faWD13Srrf7"
                    target="_blank"
                    aria-label={`View on Google Map`}
                  >
                    2055 / 56 Silver business point, VIP Circle, Uttran, Surat,
                    Gujarat 394105.
                  </Link>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <Container fluid className="mb-2">
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d4411.415353344635!2d72.8609778758452!3d21.233677080707732!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be04f6f550b5517%3A0x2ad6334c22f4729f!2sNeoverce%20IT%20Solutions!5e0!3m2!1sen!2sin!4v1750398005643!5m2!1sen!2sin"
          width="100%"
          height="450"
          loading="lazy"
          style={{ borderRadius: "10px" }}
          referrerPolicy="no-referrer"
          title="Google Map showing office location"
        />
      </Container>
    </>
  );
};

export default ContactPage;
